import { useCallback, useEffect, useState } from "react";

// Persists in-progress form state to localStorage so a page reload doesn't lose typing.
// Drop-in replacement for useState. Reloads from storage when `key` changes (e.g. when
// the user navigates between chat threads or task templates).
//
// Drafts are cleared by setting to the empty value (`""` for chat, `{}` for inputs);
// no separate clear API is needed.

function loadDraft<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveDraft<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage disabled (private browsing, etc.) — fall back to in-memory only.
  }
}

type DraftSetter<T> = (value: T | ((prev: T) => T)) => void;

export function useDraft<T>(key: string, initial: T): [T, DraftSetter<T>] {
  const [value, setValue] = useState<T>(() => loadDraft(key, initial));

  // Reload whenever the key changes (thread switch, template switch).
  useEffect(() => {
    setValue(loadDraft(key, initial));
    // We intentionally exclude `initial` from deps — only key changes should trigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const set = useCallback<DraftSetter<T>>(
    (v) => {
      setValue((prev) => {
        const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
        saveDraft(key, next);
        return next;
      });
    },
    [key],
  );

  return [value, set];
}
